import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Background } from '@appcharge/shared-ui';
import { Divider, Grid, Stack, Typography } from '@mui/material';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useTheme from 'api/useTheme';
import {
  EAssetType,
  EBundlesInternalViewModel,
  EFeatureFlag
} from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips, useUploads } from 'hooks/useUpload';
import { validateLottieFile } from 'utils/lottieAnimationUtil';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import {
  AcSelectImageItem,
  EAcSelectItemRenderType
} from 'components/AcSelect/AcSelect.types';

import { ThemeLoader } from '../ThemeLoader';
import { getFonts } from '../utils';

import { FooterSection } from './Components/FooterSection';
import { MaintenanceSection } from './Components/MaintenanceSection';
import { defaultGeneralTheme } from './defaults';
import { GeneralThemeFormValues } from './generalTheme.types';

import 'style/forms.scss';

const getSelectPictureItemsByType = ({
  type,
  pictures = [],
  defaultOption
}: {
  type: EAssetType;
  pictures?: any;
  defaultOption?: { name: string; url: string };
}): AcSelectImageItem[] => {
  const options = [
    ...(defaultOption ? [defaultOption] : []),
    ...(pictures ? pictures.filter((p: any) => p.type === type) : [])
  ];

  return options.map((picture: any) => {
    return {
      content: picture.name,
      key: picture.url,
      value: picture.url,
      url: picture.url,
      renderFunction: () => {
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <img
              src={picture.url}
              alt={picture.name}
              style={{
                width: 30,
                maxHeight: 30
              }}
            />
            <Typography>{picture.name}</Typography>
          </Stack>
        );
      }
    };
  });
};

export const GeneralSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<GeneralThemeFormValues>();

  const { publisherId } = useParams();
  const { getFeatureFlagByKey } = useFeatureFlags({ publisherId });

  const isTemplatesStudioFeatureEnabled = getFeatureFlagByKey(
    EFeatureFlag.DASHBOARD_TEMPLATES_STUDIO
  );
  const isMaintenanceModeSettingsEnabled = getFeatureFlagByKey(
    EFeatureFlag.DASHBOARD_MAINTENANCE_MODE_SETTINGS
  );

  const isStoreSEOEnabled = getFeatureFlagByKey(
    EFeatureFlag.DASHBOARD_STORE_SEO_SETTINGS
  );

  const getDefaultFormValues = () => ({
    storeTabName: defaultGeneralTheme.storeTabName,
    logo: defaultGeneralTheme.logo,
    favicon: defaultGeneralTheme.favicon,
    backgroundImageMobile: defaultGeneralTheme.backgroundImageMobile,
    backgroundImageDesktop: defaultGeneralTheme.backgroundImageDesktop,
    storeSaleImage: defaultGeneralTheme.storeSaleImage,
    loaderAnimation: defaultGeneralTheme.loaderAnimation,
    font: defaultGeneralTheme.font,
    receiptHeader: defaultGeneralTheme.receiptHeader,
    logoSize: defaultGeneralTheme.logoSize,
    popupRatio: defaultGeneralTheme.popupRatio,
    footer: defaultGeneralTheme.footer,
    maintenanceModeRichText: defaultGeneralTheme.maintenanceModeRichText,
  });

  const defaultValues = useMemo(() => {
    const defaults = getDefaultFormValues();

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      storeTabName: values.storeTabName || defaults.storeTabName,
      logo: values.logo || defaults.logo,
      favicon: values.favicon || defaults.favicon,
      backgroundImageMobile:
        values.backgroundImageMobile || defaults.backgroundImageMobile,
      backgroundImageDesktop:
        values.backgroundImageDesktop || defaults.backgroundImageDesktop,
      storeSaleImage: values.storeSaleImage || defaults.storeSaleImage,
      loaderAnimation: values.loaderAnimation || defaults.loaderAnimation,
      font: values.font || defaults.font,
      receiptHeader: values.receiptHeader || defaults.receiptHeader,
      logoSize: values.logoSize || defaults.logoSize,
      popupRatio: values.popupRatio || defaults.popupRatio,
      footer: values.footer || defaults.footer,
      maintenanceModeRichText: values.maintenanceModeRichText ?? defaults.maintenanceModeRichText,
    };
  }, [values]);

  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const pictures = useImages(publisherId).getImages;
  const addPictureInputRef = useRef<HTMLInputElement>(null);
  const [currentUploadingField, setCurrentUploadingField] = useState('');

  const { uploadImage } = useUploads();

  const [isProductMadness, setIsProductMadness] = useState<boolean>(false);
  useEffect(() => {
    const bundleViewModel = generalTheme?.data?.bundlesInternalViewModel;
    if (!bundleViewModel) return;
    setIsProductMadness(
      [
        EBundlesInternalViewModel.JACKPOT_SLOTS,
        EBundlesInternalViewModel.BIG_FISH_CASINO
      ].includes(bundleViewModel)
    );
  }, [generalTheme.data?.bundlesInternalViewModel]);

  const hasValues = useMemo(
    () => values && Object.keys(values).length > 0,
    [values]
  );

  useEffect(
    () =>
      setIsLoading(generalTheme.isLoading || !generalTheme.data || !hasValues),
    [generalTheme?.isLoading, generalTheme?.data, hasValues]
  );

  const loaderAnimationOptions = useMemo(() => {
    const defaultOption: AcSelectImageItem = {
      content: 'Default Loader',
      key: 'default-loader',
      value:
        'https://media-dev.appcharge.com/media/defaults/circular-loader.json',
      url: 'https://media-dev.appcharge.com/media/defaults/circular-loader.json',
      name: 'Default Loader'
    };

    const animations = (pictures?.data || []).filter(
      (picture: { type: EAssetType }) => picture.type === EAssetType.ANIMATION
    );

    return [
      defaultOption,
      ...animations.map(
        (picture: {
          type: EAssetType;
          name: string;
          _id: string;
          url: string;
        }): AcSelectImageItem => ({
          content: picture.name,
          key: picture._id,
          value: picture.url,
          url: picture.url,
          name: picture.name
        })
      )
    ];
  }, [pictures.data]);

  const logoItems = useMemo(
    () =>
      getSelectPictureItemsByType({
        type: EAssetType.LOGO,
        pictures: pictures.data || [],
        defaultOption: {
          name: 'Royal Blast Logo',
          url: 'https://media-sandbox.appcharge.com/media/6853f47e7289324219cb4f34/1750333478583__a1deae4a-c4e9-4478-acbf-7c71e66e3186'
        }
      }),
    [pictures.data]
  );

  const faviconItems = useMemo(
    () =>
      getSelectPictureItemsByType({
        type: EAssetType.FAVICON,
        pictures: pictures.data || [],
        defaultOption: {
          name: 'Royal Blast Favicon',
          url: 'https://media.appcharge.com/defaults/default-store-logo.svg'
        }
      }),
    [pictures.data]
  );

  const backgroundMobileImageItems = useMemo(
    () =>
      getSelectPictureItemsByType({
        type: EAssetType.BG_MOBILE,
        pictures: pictures.data || [],
        defaultOption: {
          name: 'Default Appcharge background',
          url: 'https://media-dev.appcharge.com/media/defaults/Default_Appcharge_Background_Mobile.jpeg'
        }
      }),
    [pictures.data]
  );

  const backgroundDesktopImageItems = useMemo(
    () =>
      getSelectPictureItemsByType({
        type: EAssetType.BG_DESKTOP,
        pictures: pictures.data || [],
        defaultOption: {
          name: 'Default Appcharge Background',
          url: 'https://media-sandbox.appcharge.com/media/6853f47e7289324219cb4f34/1750334375960__1534a49b-8888-48f0-be95-1341bee06448'
        }
      }),
    [pictures.data]
  );

  const storeSaleImageItems = useMemo(
    () =>
      getSelectPictureItemsByType({
        type: EAssetType.GENERAL,
        pictures: pictures.data
      }),
    [pictures.data]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          General Settings
        </Typography>
        {!pictures.isLoading && (
          <Stack>
            <AcCard stackContainer={false} title="Store Essentials">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                {!isStoreSEOEnabled && (
                  <Grid item xs={12}>
                    <AcInput
                      header="Store Tab Title"
                      name="storeTabName"
                      value={defaultValues.storeTabName}
                      tooltip="This will be the title of your store tab."
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.storeTabName && Boolean(errors.storeTabName)}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <input
                    style={{ display: 'none' }}
                    type="file"
                    onChange={(e) =>
                      uploadImage(
                        e,
                        addPictureInputRef.current as HTMLInputElement,
                        pictures,
                        setFieldValue,
                        setCurrentUploadingField,
                        currentUploadingField
                      )
                    }
                    ref={addPictureInputRef}
                    accept="image/*"
                  />
                  {!isTemplatesStudioFeatureEnabled && (
                    <AcSelect
                      header="Logo"
                      name="logo"
                      dataTestBoxId="logo"
                      value={defaultValues.logo}
                      uploadBtnTestId="uploadLogo"
                      dataTestListId="logoList"
                      optionDataTestIdPrefix="logoOption"
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('logo', url);
                        },
                        uploadType: EAssetType.LOGO,
                        uploadMessage: UploadsTooltips[EAssetType.LOGO]
                      }}
                      items={logoItems}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.logo && Boolean(errors.logo)}
                      tooltip="This will be the logo of your store."
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    header="Favicon"
                    dataTestBoxId="favicon"
                    name="favicon"
                    value={defaultValues.favicon}
                    uploadBtnTestId="uploadFavicon"
                    dataTestListId="faviconList"
                    optionDataTestIdPrefix="faviconOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('favicon', url);
                      },
                      uploadType: EAssetType.FAVICON,
                      uploadMessage: UploadsTooltips[EAssetType.FAVICON]
                    }}
                    items={faviconItems}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.favicon && Boolean(errors.favicon)}
                  />
                </Grid>
                {!isTemplatesStudioFeatureEnabled && (
                  <>
                    <Grid item xs={12}>
                      <AcSelect
                        header="Background Image (Mobile)"
                        name="backgroundImageMobile"
                        value={defaultValues.backgroundImageMobile}
                        dataTestBoxId="backgroundImageMobile"
                        uploadBtnTestId="uploadBackgroundImageMobile"
                        dataTestListId="backgroundImageMobileList"
                        optionDataTestIdPrefix="backgroundImageMobileOption"
                        uploadConfig={{
                          onUploadSuccess: async (url: string) => {
                            await pictures.refetch();
                            setFieldValue('backgroundImageMobile', url);
                          },
                          uploadType: EAssetType.BG_MOBILE,
                          uploadMessage: UploadsTooltips[EAssetType.BG_MOBILE]
                        }}
                        items={backgroundMobileImageItems}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.backgroundImageMobile &&
                          Boolean(errors.backgroundImageMobile)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <AcSelect
                        header="Background Image (Desktop)"
                        name="backgroundImageDesktop"
                        value={defaultValues.backgroundImageDesktop}
                        dataTestBoxId="backgroundImageDesktop"
                        uploadBtnTestId="uploadBackgroundImageDesktop"
                        dataTestListId="backgroundImageDesktopList"
                        optionDataTestIdPrefix="backgroundImageDesktopOption"
                        uploadConfig={{
                          onUploadSuccess: async (url: string) => {
                            await pictures.refetch();
                            setFieldValue('backgroundImageDesktop', url);
                          },
                          uploadType: EAssetType.BG_DESKTOP,
                          uploadMessage: UploadsTooltips[EAssetType.BG_DESKTOP]
                        }}
                        items={backgroundDesktopImageItems}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.backgroundImageDesktop &&
                          Boolean(errors.backgroundImageDesktop)
                        }
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <AcSelect
                    header="Loader Animation"
                    name="loaderAnimation"
                    value={defaultValues.loaderAnimation}
                    dataTestBoxId="loaderAnimation"
                    uploadBtnTestId="uploadLoaderAnimation"
                    dataTestListId="loaderAnimationList"
                    optionDataTestIdPrefix="loaderAnimationOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('loaderAnimation', url);
                      },
                      uploadType: EAssetType.ANIMATION,
                      uploadMessage: UploadsTooltips[EAssetType.ANIMATION],
                      validator: validateLottieFile
                    }}
                    items={loaderAnimationOptions}
                    renderType={EAcSelectItemRenderType.ANIMATION}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.loaderAnimation && Boolean(errors.loaderAnimation)
                    }
                  />
                </Grid>

                {isProductMadness && (
                  <Grid item xs={12}>
                    <AcSelect
                      header="Store Sale"
                      name="storeSaleImage"
                      value={defaultValues.storeSaleImage}
                      uploadBtnTestId="uploadStoreSaleImage"
                      dataTestListId="storeSaleImageList"
                      optionDataTestIdPrefix="storeSaleImageOption"
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('storeSaleImage', url);
                        },
                        uploadType: EAssetType.GENERAL,
                        uploadMessage: UploadsTooltips[EAssetType.GENERAL]
                      }}
                      items={storeSaleImageItems}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.storeSaleImage && Boolean(errors.storeSaleImage)
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </AcCard>
            <Divider />
            {!isTemplatesStudioFeatureEnabled && (
              <>
                <AcCard stackContainer={false} title="Font">
                  <Grid container rowSpacing={2} columnSpacing={1.5}>
                    <Grid item xs={12}>
                      <AcSelect
                        name="font"
                        value={defaultValues.font}
                        dataTestBoxId="font"
                        dataTestListId="fontList"
                        optionDataTestIdPrefix="fontOption"
                        items={getFonts()}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.font && Boolean(errors.font)}
                      />
                    </Grid>
                  </Grid>
                </AcCard>
                <Divider />
              </>
            )}
            <FooterSection
              isTemplatesStudioFeatureEnabled={isTemplatesStudioFeatureEnabled}
            />
            {isMaintenanceModeSettingsEnabled && (
              <>
                <Divider />
                <MaintenanceSection />
              </>
            )}
          </Stack>
        )}
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div
          className="store-preview-wrapper iphone-mock"
          style={{ overflow: 'scroll' }}
        >
          <Background
            backgroundImageMobile={defaultValues.backgroundImageMobile}
            backgroundImageDesktop={defaultValues.backgroundImageMobile}
            width="100%"
            height="100%"
            position="absolute"
          />
          <img
            style={{ position: 'relative' }}
            alt="logo"
            className={`storeLogo ${defaultValues.logoSize}`}
            src={defaultValues.logo}
          />
        </div>
      </Grid>
    </Grid>
  );
};
